import { Tag } from '@carbon/react';
import { Link } from 'components/link';
import {
  subtractCoverageSuffix,
  tractRowCoverageLabel,
} from 'utils/subtract-coverage';
import { EMPTY } from './utils';
import type { AssetKind, AssetRow } from './types';

/** The label a non-tract row's kind reads as. */
const KIND_LABELS: Record<Exclude<AssetKind, 'tract'>, string> = {
  wellbore: 'Wellbore',
  ltd_slot: 'LTD',
};

/**
 * The Assets table's Name cell. The table lists every asset on the package, so a row can be
 * something other than a CRM tract, and can be an asset that adds nothing to the evaluation.
 *
 * - A tract links to its CRM Tract edit page, as it always has. One Salesforce doesn't have yet
 *   has no name, so it reads as its id, unlinked. One the package holds in part says how much
 *   of it the package holds.
 * - A wellbore or LTD asset links to its well, or the unit its slot is in, and says which it is.
 * - An asset with nothing behind it — no slots, no calculated interest — says why on the row
 *   itself. It has nothing to expand, so the row is the only place the reason can go.
 *
 * @param {object} props - The adapted asset row.
 * @param {AssetRow} props.row - The row to name.
 * @return {JSX.Element} The name, its link, and any kind / reason tags.
 */
export function AssetNameCell({ row }: { row: AssetRow }) {
  const kind =
    row.assetKind && row.assetKind !== 'tract' ? row.assetKind : null;
  const label = row.name || row.salesforceId;
  const held = tractRowCoverageLabel(row);
  let to: string | undefined;
  if (kind) {
    to = row.assetHref;
  } else if (row.name && row.salesforceId) {
    // The route is polymorphic on tract/subtract salesforce ids.
    to = `/crm/tracts_and_subtracts/${encodeURIComponent(
      row.salesforceId
    )}/edit`;
  }

  return (
    <>
      {to && label ? (
        <Link to={to} openInNewTab onClick={(e) => e.stopPropagation()}>
          {label}
        </Link>
      ) : (
        label || EMPTY
      )}
      {subtractCoverageSuffix(held)}
      {kind && (
        <Tag size="sm" type="cool-gray">
          {KIND_LABELS[kind]}
        </Tag>
      )}
      {row.noSlotsReason && (
        <Tag size="sm" type="gray">
          {row.noSlotsReason}
        </Tag>
      )}
    </>
  );
}
